import GroupMemberRecord from "models/view-models/group-member-record";
import React, { useEffect, useState } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { t } from "utilities/localization-utils";
import GroupMemberService from "utilities/services/groups/group-member-service";
import { ToastManager } from "utilities/toast/toast-manager";
import Button from "atoms/buttons/button";
import PreviewTeamMembersModal from "./preview-team-members-modal";
import { ButtonStyles } from "atoms/constants/button-styles";
import useModalActions from "utilities/hooks/use-modal-actions";
import Paragraph from "atoms/typography/paragraph";
import StringUtils from "utilities/string-utils";
import Loader from "atoms/loaders/loader";

interface PreviewTeamMembersProps {
    groupId: number;
    groupName: string;
}

const BASE_CSS_CLASS_NAME = "c-preview-team-members";
const MIN_NUMBER_OF_NAMES_SHOWN = 2;
const MAX_NUMBER_OF_NAMES_SHOWN = 3;

const PreviewTeamMembers: React.FC<PreviewTeamMembersProps> = ({
    groupId,
    groupName,
}) => {
    const [groupMembers, setGroupMembers] = useState<GroupMemberRecord[]>();
    const { list: getGroupMembersApi } = GroupMemberService.useList();
    const { handleClose, handleOpen, isOpen } = useModalActions();
    const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(true);

    const groupMembersPreviewText = groupMembers
        ? groupMembers.length <= MAX_NUMBER_OF_NAMES_SHOWN
            ? StringUtils.formatToList(
                  groupMembers.map((groupMember) =>
                      groupMember.getFirstAndLastName()
                  )
              )
            : StringUtils.formatToList([
                  ...groupMembers
                      .slice(0, MAX_NUMBER_OF_NAMES_SHOWN)
                      .map((groupMember) => groupMember.getFirstAndLastName()),
                  "",
              ])
        : "";

    const qualifierText = groupMembers
        ? groupMembers.length <= MAX_NUMBER_OF_NAMES_SHOWN
            ? ""
            : groupMembers?.length === MAX_NUMBER_OF_NAMES_SHOWN + 1
              ? t("oneOther")
              : t("numberOthers", {
                    number: groupMembers.length - MAX_NUMBER_OF_NAMES_SHOWN,
                })
        : "";

    useEffect(() => {
        const listGroupMembersApi = async () => {
            if (groupId) {
                try {
                    setIsLoadingTeamMembers(true);
                    const { resultObjects } = await getGroupMembersApi({
                        groupId,
                    });
                    if (CollectionUtils.isEmpty(resultObjects)) {
                        setGroupMembers([]);
                        return;
                    }
                    setGroupMembers(resultObjects);
                } catch (error) {
                    ToastManager.error(
                        t("errors-actionResource", {
                            action: t("loading"),
                            resource: t("team-member_plural"),
                        })
                    );
                } finally {
                    setIsLoadingTeamMembers(false);
                }
            }
        };
        listGroupMembersApi();
    }, [getGroupMembersApi, groupId]);
    return isLoadingTeamMembers ? (
        <Loader />
    ) : (
        CollectionUtils.hasValues(groupMembers) && (
            <>
                {groupMembers.length >= MIN_NUMBER_OF_NAMES_SHOWN && (
                    <Paragraph
                        dataTestId="preview-text"
                        cssClassName={`${BASE_CSS_CLASS_NAME}__text`}>
                        {groupMembersPreviewText}
                        {qualifierText && <strong>{qualifierText}</strong>}.
                    </Paragraph>
                )}
                <Button
                    style={ButtonStyles.None}
                    onClick={handleOpen}
                    cssClassName={`${BASE_CSS_CLASS_NAME}__button`}>
                    {t("viewTeamDetails")}
                </Button>
                <PreviewTeamMembersModal
                    closeDialog={handleClose}
                    groupName={groupName}
                    groupMembers={groupMembers}
                    isVisible={isOpen}
                    label={t("team-member_plural")}
                />
            </>
        )
    );
};

export default PreviewTeamMembers;
